// Config-driven register page: a list table plus an add/edit form and delete,
// shared by the customer/site/asset registers. Plain English, zero jargon -- the
// office manager uses this. Validation errors come straight from the API.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerPage } from "../../../lib/useServerPage";
import { useDebouncedValue } from "../../../lib/useDebouncedValue";
import { Pager } from "../../../components/Pager";
import type { FormEvent, ReactNode } from "react";
import { Plus } from "lucide-react";
import { ApiError } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { erasedPersonRefusal } from "../../../lib/erasedPerson";
import { ErasedPersonNotice } from "../../../components/ErasedPersonNotice";
import type { Resource } from "../../../lib/resources";
import { ConfirmDeleteModal } from "../../../components/ConfirmDeleteModal";
import { ConfirmButton } from "../../../components/ConfirmButton";
import { PostcodeLookup } from "../../../components/PostcodeLookup";
import { SwipeListRow } from "../../../components/SwipeListRow";
import { applyTableView } from "./registerTable";
import type { SortDir } from "./registerTable";
import { useSortableColumn } from "../../../lib/useSortableColumn";
import { PageHeader } from "../../../components/PageHeader";
import { Button } from "../../../components/Button";
import { RecordPicker } from "../../../components/RecordPicker";
import { Loader } from "../../../components/Loader";
import { EmptyState } from "../../../components/EmptyState";
import { ExportCsvMenu } from "../../../components/ExportCsvMenu";
import type { ExportListKey } from "../../../lib/api";

export interface FieldDef {
  name: string;
  label: string;
  type: "text" | "email" | "number" | "date" | "textarea" | "select" | "postcode-lookup" | "record-picker";
  required?: boolean;
  options?: { value: string; label: string }[];
  help?: ReactNode;
  // For a "postcode-lookup" field (Session AV, Item 3): the name of the address field this postcode
  // fills when the office picks an address from the lookup dropdown.
  addressField?: string;
  // For a "record-picker" field (Session SC2A): which register the typeahead searches. The form value is the chosen id.
  pickerKind?: "customer" | "site";
}

export interface ColumnDef<T> {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  // Opt a column into client-side sorting + text search (Session AD, Item 2): the comparable /
  // searchable value for a row (a name, a date string, a count). Omit for a column that should not be
  // sortable (e.g. a status dot or a complex badge). The search box appears once any column has one.
  sortValue?: (row: T) => string | number | null | undefined;
  // Optional column width as a CSS value (e.g. "24%" or "8rem") for the fixed table layout (Session
  // AJ, Item 4). Columns without a width share the remaining space evenly. Over-long text in any
  // column is truncated with an ellipsis so the table never needs a horizontal scrollbar.
  width?: string;
  // Set true for a column whose cell renders a badge, a button or other content that must stay
  // fully visible (it opts the cell out of the fixed-layout truncation via .cell-actions).
  noTruncate?: boolean;
}

// A named sort preset for the optional "Sort by" dropdown (e.g. the Lifts list). `key` is a column
// key (or null for the default load order); `dir` is the direction that preset applies.
export interface SortOption {
  label: string;
  key: string | null;
  dir: SortDir;
}

// The sentinel value for the "Custom order" placeholder shown when the active sort (e.g. set by a
// header click to descending) does not match any named preset. A "/"-free string so it never
// collides with a real "key:dir" preset value.
const CUSTOM_SORT_VALUE = "__custom__";

// Session SC2C: how many records a server-paged register shows at a time.
const REGISTER_PAGE_SIZE = 50;

interface RegisterPageProps<T> {
  title: string;
  subtitle?: string;
  noun: string;
  // Optional explicit label for the primary add button (e.g. "Add Customer").
  // Defaults to "Add {noun}". Lets a page make its primary action read exactly
  // right without diverging from the shared, consistent button styling.
  addLabel?: string;
  resource: Resource<T>;
  columns: ColumnDef<T>[];
  fields: FieldDef[];
  rowId: (row: T) => string;
  toForm: (row: T) => Record<string, string>;
  query?: Record<string, string>;
  emptyForm: Record<string, string>;
  // Read-only mode: no add button, no per-row edit/delete (e.g. Customers, which
  // are created automatically when a quote is accepted). An optional note explains
  // why to the office.
  readOnly?: boolean;
  note?: ReactNode;
  // Optional filter UI rendered above the table, and a predicate applied to the
  // displayed rows (e.g. the Lifts compliance / due-this-month filters). The data
  // is still loaded in full; filtering is client-side over what is shown.
  toolbar?: ReactNode;
  filterRow?: (row: T) => boolean;
  // Optional named "Sort by" presets shown as a dropdown next to the search box (e.g. the Lifts
  // list: "Lift ID (default)", "Next due (soonest first)"). They drive the same sort state as the
  // clickable column headers. Omit for the header-click sort only.
  sortOptions?: SortOption[];
  // When provided, the per-row Delete uses a two-step CLICK confirm dialog (Session AJ Item 5):
  // clicking Delete opens a dialog that shows what will be removed (the `message`, e.g. a dependency
  // summary for a cascade delete) and an optional amber `warning`, and a second click confirms. No
  // typing. The server's 409 blocker message surfaces in the dialog. Omit for the simple
  // window.confirm delete.
  deleteConfirm?: (row: T) => { message: string; warning?: string };
  // When provided, the whole row is clickable and calls this on click (the list becomes a clean
  // scan-and-drill-in surface, e.g. the Lifts register navigating to /office/lifts/:id). The
  // per-row Edit/Delete actions column is then suppressed: edit and delete live on the detail page.
  // RegisterPage stays router-free; the caller supplies navigation (useNavigate). A cell can carry
  // its own interactive element (e.g. a real Link) as long as it stops propagation.
  onRowClick?: (row: T) => void;
  // Session ACW (mobile usability): the column KEYS to keep visible below the md breakpoint. Every
  // OTHER column's col/th/td gains max-md:hidden so a phone shows only the primary identifier plus
  // a status/figure column or two, never a horizontally-cramped full table. Omit for a page that has
  // not been reviewed for mobile yet (every column stays visible at every width, unchanged). The
  // built-in Edit/Delete actions column (showActions) is deliberately NEVER hidden by this: for a
  // register with no detail-page edit path (e.g. Sites), the inline actions are the only way to
  // manage a row, so they must survive down to phone width.
  mobileColumns?: string[];
  // An aria-label for the table when mobileColumns is active, so a screen reader on a phone (where
  // several header cells are visually hidden) still announces what the list represents.
  mobileLabel?: string;
  // Mobile two-line rows (favicon-sites patch): when provided, BELOW the md breakpoint the desktop
  // table is hidden and each row renders as a two-line swipe row instead (title on line 1, a muted
  // subtitle + a meta/status on line 2, actions revealed by swiping left, a tap navigates via
  // onTap). Desktop (md and up) is byte-identical: the table is untouched and this list is md:hidden.
  // The revealed Edit/Delete reuse the same handlers as the desktop actions column (startEdit and the
  // deleteConfirm dialog / inline remove), so behaviour matches; it is only rendered when the page has
  // an inline actions column (showActions), never on a read-only or row-click register.
  mobileRow?: (row: T) => MobileRowSpec;
  // Session SC2C: read the register ONE PAGE (50) at a time from the server, which filters (the `query` parameters), searches (the search box's
  // text) and sorts (the column headers) and answers the TRUE total of every match. The page then shows rows exactly as before, with a pager
  // below. Without this prop the page loads the whole list and sorts, searches and filters in the browser, as it always did (the registers'
  // own tests, and any page not yet moved).
  serverPaged?: boolean;
  // Session GAP9: which CSV export this register offers, in a More actions menu in the header (server-paged registers only). The export is the list as
  // it is showing: the same `query`, search and sort. Omit for a register with no export.
  exportList?: ExportListKey;
}

export interface MobileRowSpec {
  title: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
  onTap?: () => void;
}

export function RegisterPage<T>(props: RegisterPageProps<T>) {
  const { title, subtitle, noun, addLabel, resource, columns, fields, rowId, toForm, query, emptyForm, readOnly, note, toolbar, filterRow, sortOptions, deleteConfirm, onRowClick, mobileColumns, mobileLabel, mobileRow, serverPaged, exportList } = props;
  // A column not in the mobileColumns allow-list gets max-md:hidden; when mobileColumns is not
  // supplied at all, every column stays visible (the pre-ACW behaviour, byte-identical).
  const mobileHidden = (key: string) => (mobileColumns && !mobileColumns.includes(key) ? " max-md:hidden" : "");
  // The mobile chevron disclosure column only makes sense for a table whose ROW already navigates
  // (onRowClick) -- a register like Sites/Customers where only the Name cell's own Link navigates
  // keeps that Link as its mobile tap target instead (adding a second, competing full-row nav would
  // change desktop behaviour, which must stay pixel-identical).
  const showChevron = Boolean(mobileColumns && onRowClick);
  // When rows navigate to a detail page (onRowClick), the detail page owns Edit/Delete, so the
  // per-row actions column is hidden.
  const showActions = !readOnly && !onRowClick;

  const [looseItems, setItems] = useState<T[]>([]);
  const [looseLoading, setLoading] = useState(true);
  const [looseListError, setListError] = useState<string | null>(null);
  // A failed inline delete (the fallback ConfirmButton path) surfaces here as a role=alert banner
  // above the list, instead of a native window.alert (Session AEH, FIX-1 A1).
  const [removeError, setRemoveError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<Record<string, string>>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);
  // Session ASQ, Item 3's review, F5: a create refused because a detail belongs to a person whose data was erased (409
  // erased_person). The server's own sentence, and ONE explicit "Create anyway" that resends with createAnyway.
  const [erased, setErased] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const queryKey = query ? JSON.stringify(query) : "";
  const load = useCallback(async () => {
    if (serverPaged) return; // a paged register is re-read by serverPage.reload() below
    setLoading(true);
    setListError(null);
    try {
      setItems(await resource.list(query));
    } catch {
      setListError("Could not load the list. Please try again.");
    } finally {
      setLoading(false);
    }
    // resource and query are stable per page render; queryKey captures query changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, queryKey, serverPaged]);

  useEffect(() => {
    void load();
  }, [load]);

  function startAdd() {
    setForm(emptyForm);
    setFieldErrors({});
    setFormError(null);
    setErased(null);
    setEditingId(null);
    setAdding(true);
  }

  function startEdit(row: T) {
    setForm(toForm(row));
    setFieldErrors({});
    setFormError(null);
    setAdding(false);
    setEditingId(rowId(row));
  }

  function cancel() {
    setAdding(false);
    setEditingId(null);
    setFieldErrors({});
    setFormError(null);
    setErased(null);
  }

  async function save(createAnyway: boolean) {
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    setErased(null);
    try {
      if (editingId) await resource.update(editingId, form);
      else await resource.create(createAnyway ? { ...form, createAnyway: true } : form);
      cancel();
      await reloadList();
    } catch (err) {
      const refusal = editingId ? null : erasedPersonRefusal(err);
      if (refusal) setErased(refusal.message);
      else if (err instanceof ApiError && err.details) setFieldErrors(err.details);
      else setFormError(friendlyApiError(err, "Could not save. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    // The ordinary save never goes ahead past the erased-person warning: only its own button does.
    void save(false);
  }

  // The fallback delete (used when a consumer does not pass deleteConfirm) is fired by an inline
  // two-step ConfirmButton (Session AR Item 3), so it no longer needs a native window.confirm.
  async function remove(row: T) {
    setRemoveError(null);
    try {
      await resource.remove(rowId(row));
      await reloadList();
    } catch (err) {
      // Surface the failure inline (role=alert banner above the list), never a native window.alert.
      setRemoveError(friendlyApiError(err, "Could not delete. Please try again."));
    }
  }

  // Click-confirm delete (used when deleteConfirm is provided): a row pending deletion, its
  // in-flight flag, and the server's plain-English error (e.g. the 409 blocker list).
  const [deletingRow, setDeletingRow] = useState<T | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  function startDelete(row: T) {
    setDeleteError(null);
    setDeletingRow(row);
  }

  async function confirmTypedDelete() {
    if (!deletingRow) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await resource.remove(rowId(deletingRow));
      setDeletingRow(null);
      await reloadList();
    } catch (err) {
      // The server's 409 carries a plain-English message (ApiError.code); show it in the dialog.
      setDeleteError(friendlyApiError(err, "Could not delete. Please try again."));
    } finally {
      setDeleteBusy(false);
    }
  }

  const deleteSpec = deletingRow && deleteConfirm ? deleteConfirm(deletingRow) : null;

  // ---- Column sort + text search (Session AD, Item 2) ----
  // Available only when at least one column opts in with a sortValue accessor; consumers that have
  // not adopted it are completely unchanged (no search box, no sortable headers). The active sort now
  // PERSISTS per page for the session via sessionStorage (Session AU, Item 3), keyed on the page title.
  const { sortKey, sortDir, toggle: toggleSort, setSort } = useSortableColumn(`register:${title}`);
  // Seed the search box from a ?q= URL param (Session AE, Item 2): the global search "Search all
  // [type]" link lands here with the term pre-filled. Read once on mount; no router coupling.
  const [search, setSearch] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("q") ?? "";
  });
  const sortable = columns.some((c) => c.sortValue);

  const formOpen = adding || editingId !== null;
  // Session SC2C: in server-paged mode the SERVER filters, searches and sorts, and this page asks for one page of 50. The search text is sent
  // after a short pause; a change of search, sort or filter goes back to the first page.
  const debouncedSearch = useDebouncedValue(search.trim(), 250);
  const serverQueryKey = JSON.stringify({ query, q: debouncedSearch, sortKey, sortDir });
  const serverPage = useServerPage(
    serverQueryKey,
    (at) =>
      resource.listPage!({
        ...(query ?? {}),
        ...(debouncedSearch ? { q: debouncedSearch } : {}),
        ...(sortKey ? { sort: sortKey, dir: sortDir } : {}),
        limit: String(REGISTER_PAGE_SIZE),
        offset: String(at),
      }),
    Boolean(serverPaged),
  );
  const loading = serverPaged ? serverPage.loading : looseLoading;
  const listError = serverPaged ? (serverPage.error ? "Could not load the list. Please try again." : null) : looseListError;
  const items: T[] = serverPaged ? (serverPage.data?.items ?? []) : looseItems;
  const serverTotal = serverPaged ? (serverPage.data?.total ?? 0) : items.length;
  const reloadList = () => (serverPaged ? serverPage.reload() : load());
  const filtered = useMemo(() => (filterRow && !serverPaged ? items.filter(filterRow) : items), [items, filterRow, serverPaged]);
  // Search + sort on top of any page-specific filterRow (filter -> search -> sort). Server-paged: the rows arrive already searched and sorted.
  const displayed = useMemo(
    () => (serverPaged ? items : applyTableView(filtered, columns, { sortKey, sortDir, search })),
    [serverPaged, items, filtered, columns, sortKey, sortDir, search],
  );
  const searching = search.trim().length > 0;
  // The current sort state as a "key:dir" string, and whether it matches one of the named presets
  // (so the "Sort by" dropdown can show "Custom order" when a header click set an off-preset state).
  const sortStateValue = sortKey === null ? "" : `${sortKey}:${sortDir}`;
  const sortPresetMatch = !sortOptions || sortOptions.some((o) => (o.key === null ? "" : `${o.key}:${o.dir}`) === sortStateValue);

  return (
    <div>
      {/* Session ACW (mobile usability): below sm the title takes its own full-width line and the
          action button stacks underneath, instead of squeezing onto one row; sm and up keep the
          existing side-by-side layout byte-identical. */}
      <PageHeader
        className="mb-4"
        title={title}
        subtitle={subtitle}
        actions={
          (!readOnly && !formOpen) || (serverPaged && exportList) ? (
            <>
              {!readOnly && !formOpen && (
                <Button onClick={startAdd} icon={<Plus aria-hidden size={15} />}>
                  {addLabel ?? `Add ${noun}`}
                </Button>
              )}
              {serverPaged && exportList && (
                <ExportCsvMenu
                  list={exportList}
                  params={{ ...(query ?? {}), ...(debouncedSearch ? { q: debouncedSearch } : {}), ...(sortKey ? { sort: sortKey, dir: sortDir } : {}) }}
                />
              )}
            </>
          ) : undefined
        }
      />

      {note && (
        <p className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-4 py-3 text-sm text-ele-body">
          {note}
        </p>
      )}

      {toolbar && <div className="mb-4">{toolbar}</div>}

      {/* Sort + search controls (Session AD, Item 2). Shown only when a column opts into sorting; a
          "Sort by" dropdown (when the page supplies named presets) on the left, the text search on
          the right. The data is already loaded, so both are instant and client-side. */}
      {sortable && !loading && !listError && (serverPaged ? serverTotal > 0 || search.trim().length > 0 || query !== undefined : items.length > 0) && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            {sortOptions && sortOptions.length > 0 && (
              <label className="flex items-center gap-2 text-sm">
                <span className="font-menu text-xs uppercase tracking-wide text-ele-body">Sort by</span>
                <select
                  // A column-header click can set a sort (e.g. descending) that no named preset
                  // carries; show an honest "Custom order" placeholder then rather than rendering a
                  // blank or mis-selected control (Session AD Item 2 review).
                  value={sortPresetMatch ? sortStateValue : CUSTOM_SORT_VALUE}
                  onChange={(e) => {
                    if (e.target.value === CUSTOM_SORT_VALUE) return;
                    const opt = sortOptions.find((o) => `${o.key}:${o.dir}` === e.target.value);
                    setSort(opt?.key ?? null, opt?.dir ?? "asc");
                  }}
                  className="rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-1.5 text-sm outline-none focus:border-ele-green"
                >
                  {!sortPresetMatch && (
                    <option value={CUSTOM_SORT_VALUE} disabled>
                      Custom order
                    </option>
                  )}
                  {sortOptions.map((o) => (
                    <option key={o.label} value={o.key === null ? "" : `${o.key}:${o.dir}`}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {/* Session ACW: the search box goes full width below sm (it sits under the header on a
              phone), reverting to its fixed w-56 from sm up. */}
          <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              aria-label={`Search ${noun}s`}
              className="w-full min-w-0 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-1.5 text-sm outline-none focus:border-ele-green sm:w-56"
            />
            {searching && (
              <button onClick={() => setSearch("")} className="shrink-0 font-menu text-xs tracking-wide text-ele-green hover:underline">
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {formOpen && (
        <form
          onSubmit={submit}
          className="mb-6 rounded-ele border border-ele-purple-accent bg-ele-surface p-5"
        >
          <h2 className="mb-4 font-heading text-xl text-ele-blue">
            {editingId ? `Edit ${noun}` : `New ${noun}`}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map((field) => (
              <div key={field.name} className={field.type === "textarea" ? "sm:col-span-2" : ""}>
                <label className="mb-1 block text-sm" htmlFor={field.name}>
                  {field.label}
                  {field.required && <span className="text-ele-green"> *</span>}
                </label>
                {field.type === "select" ? (
                  <select
                    id={field.name}
                    value={form[field.name] ?? ""}
                    onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                  >
                    {(field.options ?? []).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : field.type === "record-picker" && field.pickerKind ? (
                  <RecordPicker
                    kind={field.pickerKind}
                    id={field.name}
                    value={form[field.name] ?? ""}
                    onChange={(id) => setForm({ ...form, [field.name]: id })}
                  />
                ) : field.type === "textarea" ? (
                  <textarea
                    id={field.name}
                    value={form[field.name] ?? ""}
                    onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                    rows={3}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                  />
                ) : field.type === "postcode-lookup" ? (
                  <PostcodeLookup
                    id={field.name}
                    postcode={form[field.name] ?? ""}
                    onPostcodeChange={(v) => setForm({ ...form, [field.name]: v })}
                    onSelect={(r) => setForm({ ...form, [field.name]: r.postcode || form[field.name] || "", ...(field.addressField ? { [field.addressField]: r.address } : {}) })}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                  />
                ) : (
                  <input
                    id={field.name}
                    type={field.type}
                    value={form[field.name] ?? ""}
                    onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                  />
                )}
                {field.help && <p className="mt-1 text-xs text-ele-body">{field.help}</p>}
                {fieldErrors[field.name]?.map((msg) => (
                  <p key={msg} role="alert" className="mt-1 text-xs text-ele-strong">
                    {msg}
                  </p>
                ))}
              </div>
            ))}
          </div>

          {formError && (
            <p role="alert" className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {formError}
            </p>
          )}
          {erased && <ErasedPersonNotice message={erased} busy={saving} onGoAhead={() => void save(true)} />}

          <div className="mt-5 flex gap-3">
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
            <button type="button" onClick={cancel} className="rounded-ele border border-ele-purple-accent px-4 py-2">
              Cancel
            </button>
          </div>
        </form>
      )}

      {removeError && (
        <p role="alert" className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {removeError}
        </p>
      )}

      {loading ? (
        <Loader />
      ) : listError ? (
        <div role="alert" className="text-sm text-ele-strong">
          <p>{listError}</p>
          <Button variant="secondary" size="sm" className="mt-2" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : items.length === 0 && !(serverPaged && (searching || query !== undefined)) ? (
        <EmptyState message={readOnly ? `No ${noun}s yet.` : `No ${noun}s yet. Use "Add ${noun}" to create the first one.`} />
      ) : displayed.length === 0 && searching && (serverPaged || filtered.length > 0) ? (
        // The search emptied the list (rows existed before it); offer to clear just the search. When
        // a page filter (filterRow) already emptied the list, fall through to the filter message so
        // the user is not told the search is responsible (Session AD Item 2 review).
        <EmptyState
          role="status"
          message={`No results for "${search.trim()}".`}
          action={
            <Button variant="ghost" size="sm" onClick={() => setSearch("")}>
              Clear filter
            </Button>
          }
        />
      ) : displayed.length === 0 ? (
        <EmptyState role="status" message={`No ${noun}s match this filter.`} />
      ) : (
        <>
        {/* When a mobile two-line variant is supplied, the desktop table hides below md (a phone gets
            the swipe-row list instead); at md and up it renders exactly as before. */}
        <div className={`overflow-hidden rounded-ele border border-ele-purple-accent${mobileRow ? " max-md:hidden" : ""}`}>
          {/* Fixed table layout (Session AJ, Item 4): the table fills the container width and never
              scrolls horizontally. Column widths come from the colgroup (or share evenly); over-long
              cell text truncates with an ellipsis via .ele-list-table. The actions column opts out of
              truncation so its buttons stay fully visible. */}
          <table className="ele-list-table text-left text-sm" aria-label={mobileLabel}>
            <colgroup>
              {columns.map((c) => (
                <col key={c.key} className={mobileHidden(c.key) || undefined} style={c.width ? { width: c.width } : undefined} />
              ))}
              {showActions && <col style={{ width: "8rem" }} />}
              {showChevron && <col className="w-8 md:hidden" />}
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                {columns.map((c) =>
                  c.sortValue ? (
                    <th
                      key={c.key}
                      className={`px-3 py-2${mobileHidden(c.key)}`}
                      aria-sort={sortKey === c.key ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort(c.key)}
                        className="inline-flex max-w-full items-center gap-1 font-menu tracking-widest text-ele-blue transition hover:text-ele-green"
                        aria-label={`Sort by ${c.label}`}
                      >
                        <span className="min-w-0 truncate">{c.label}</span>
                        <span aria-hidden className="text-[10px]">
                          {sortKey === c.key ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                        </span>
                      </button>
                    </th>
                  ) : (
                    <th key={c.key} className={`px-3 py-2${mobileHidden(c.key)}`}>
                      {c.label}
                    </th>
                  ),
                )}
                {showActions && <th className="cell-actions px-3 py-2 text-right">Actions</th>}
                {showChevron && <th className="w-8 cell-actions md:hidden" aria-hidden="true" />}
              </tr>
            </thead>
            <tbody>
              {displayed.map((row) => (
                <tr
                  key={rowId(row)}
                  className={`border-t border-ele-purple-accent${onRowClick ? " group cursor-pointer transition hover:bg-ele-surface" : ""}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={`px-3 py-2 align-top${c.noTruncate ? " cell-actions" : ""}${mobileHidden(c.key)}`}>
                      {c.render(row)}
                    </td>
                  ))}
                  {showActions && (
                    <td className="cell-actions px-3 py-2 text-right align-top">
                      {/* max-sm: these are zero-padding text links; bump the tap box to 44px on phones only. */}
                      <button
                        onClick={() => startEdit(row)}
                        className="mr-3 text-ele-green hover:underline max-sm:inline-flex max-sm:min-h-11 max-sm:items-center max-sm:px-1"
                      >
                        Edit
                      </button>
                      {deleteConfirm ? (
                        <button
                          onClick={() => startDelete(row)}
                          className="text-ele-danger hover:underline max-sm:inline-flex max-sm:min-h-11 max-sm:items-center max-sm:px-1"
                        >
                          Delete
                        </button>
                      ) : (
                        <ConfirmButton onConfirm={() => remove(row)} label="Delete" className="text-ele-danger hover:underline max-sm:px-1" />
                      )}
                    </td>
                  )}
                  {showChevron && (
                    <td className="cell-actions md:hidden pr-1 text-right">
                      <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                        <path d="M6 4l4 4-4 4z" />
                      </svg>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile two-line swipe list (favicon-sites patch): md:hidden so desktop never sees it.
            Each row reuses the same Edit/Delete handlers as the desktop actions column. */}
        {mobileRow && (
          <ul className="overflow-hidden rounded-ele border border-ele-purple-accent md:hidden" aria-label={mobileLabel}>
            {displayed.map((row) => {
              const m = mobileRow(row);
              return (
                <SwipeListRow
                  key={rowId(row)}
                  title={m.title}
                  subtitle={m.subtitle}
                  meta={m.meta}
                  onTap={m.onTap}
                  onEdit={showActions ? () => startEdit(row) : undefined}
                  onDelete={showActions ? () => (deleteConfirm ? startDelete(row) : void remove(row)) : undefined}
                  ariaLabel={typeof m.title === "string" ? `${noun} ${m.title}` : undefined}
                />
              );
            })}
          </ul>
        )}
        </>
      )}
      {/* Session SC2C: 50 at a time; the total is the server's count of every record matching the search and filters. */}
      {serverPaged && !loading && !listError && (
        <div className={serverPage.refreshing ? "opacity-60" : undefined}>
          <Pager offset={serverPage.offset} total={serverTotal} shown={items.length} pageSize={REGISTER_PAGE_SIZE} onOffset={serverPage.setOffset} noun={[noun, `${noun}s`]} />
        </div>
      )}

      {deleteSpec && (
        // Two-step CLICK confirm (Session AJ Item 5): clicking Delete on the row opens this dialog,
        // which shows what will be removed (the dependency summary), and a second click confirms.
        // No typing. The server's 409 blocker message surfaces in the dialog.
        <ConfirmDeleteModal
          open={deletingRow !== null}
          title={`Delete ${noun}?`}
          message={deleteSpec.message}
          warning={deleteSpec.warning}
          confirmLabel={`Delete ${noun}`}
          busy={deleteBusy}
          error={deleteError}
          onConfirm={confirmTypedDelete}
          onCancel={() => {
            setDeletingRow(null);
            setDeleteError(null);
          }}
        />
      )}
    </div>
  );
}

// Render helper: show a real empty state, never "--" (house rule).
export function orNone(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "None";
  return String(value);
}

// Map a stored enum/select value back to its plain-English label for display, so
// office tables never show machine keys like "ceiling_hoist" or "six_monthly".
export function valueToLabel(
  options: { value: string; label: string }[],
  value: string | null | undefined,
): string {
  if (value === null || value === undefined || value === "") return "None";
  return options.find((o) => o.value === value)?.label ?? String(value);
}
